/**
 * PATH: packages/ui/src/hooks/useHubRouteMemory.ts
 * FILE_ID: UI-HOOK-HUB-ROUTE-MEMORY-002
 * PURPOSE: localStorage persistence + React hooks for per-hub last-sub-route
 *          memory. `useRecordHubVisit` (mounted once at the shell root)
 *          records the operator's position per hub; `useHubRouteMemory`
 *          (consumed by nav surfaces) resolves a click to the remembered
 *          screen so every tenant resumes hubs the same way.
 * WHY: See ./hubRouteMemory.helpers.ts header — operator parity ask
 *      (2026-06-15). This file is the thin, side-effecting wrapper; all
 *      decision logic lives in the pure helpers so it stays testable.
 * FLOW:
 *   useRecordHubVisit  → location change → recordHubVisit(scope, fullPath)
 *   useHubRouteMemory  → read map (per nav) → resolveTarget(to) via helpers
 * DEPENDENCIES:
 *   - react / react-router-dom: useLocation (already a @quotech/ui dep).
 *   - ./hubRouteMemory.helpers: pure bucketing + resume decision.
 *   - ./useLastRouteResume: tenantScopeFromPath + shouldRecordRoute (reuse —
 *     same scoping + transient-path filter the cold-boot resume already uses).
 */

import { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import {
  hubBucketOf,
  hubRouteMapKey,
  pickHubTarget,
} from './hubRouteMemory.helpers'
import { shouldRecordRoute, tenantScopeFromPath } from './useLastRouteResume'

type HubRouteMap = Record<string, string>

/** Per-tenant scope from the live full URL (matches useLastRouteResume). */
function currentScope(): string {
  if (typeof window === 'undefined') return 'default'
  return tenantScopeFromPath(window.location.pathname)
}

export function readHubMap(scope: string): HubRouteMap {
  try {
    if (typeof window === 'undefined') return {}
    const raw = window.localStorage.getItem(hubRouteMapKey(scope))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: HubRouteMap = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

function writeHubMap(scope: string, map: HubRouteMap): void {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(hubRouteMapKey(scope), JSON.stringify(map))
  } catch {
    /* quota / disabled storage — fail soft, never break navigation */
  }
}

/**
 * Record the operator's current full path under its hub bucket. No-op for
 * the bare root and transient auth/onboarding paths (so a hub never resumes
 * to a sign-in handshake). Never throws.
 */
export function recordHubVisit(scope: string, fullPath: string): void {
  const pathname = fullPath.split('?')[0]
  if (!shouldRecordRoute(pathname)) return
  const bucket = hubBucketOf(fullPath)
  if (!bucket) return
  const map = readHubMap(scope)
  if (map[bucket] === fullPath) return
  map[bucket] = fullPath
  writeHubMap(scope, map)
}

/**
 * Resolve a nav target to the remembered screen for its hub (or the target
 * itself). Pure read over an in-memory map snapshot.
 */
export function resolveHubTarget(
  to: string,
  map: HubRouteMap,
  navTargets: string[],
): string {
  const bucket = hubBucketOf(to)
  const stored = bucket ? map[bucket] : null
  return pickHubTarget({ to, stored, navTargets })
}

/**
 * Mount ONCE at the shell root. Records every route change into per-hub
 * memory so the next click on that hub's nav item can resume it. Capturing
 * at the shell root (not per nav surface) means narrow-viewport navigations
 * — where the desktop sidebar is unmounted — are still recorded.
 */
export function useRecordHubVisit(): void {
  const location = useLocation()
  useEffect(() => {
    if (typeof window === 'undefined') return
    recordHubVisit(currentScope(), `${location.pathname}${location.search}`)
  }, [location.pathname, location.search])
}

export interface HubRouteMemory {
  /** Resolve a nav item's `to` to its remembered screen (or `to` itself). */
  resolveTarget: (to: string) => string
}

/**
 * Consumed by nav surfaces (Sidebar / MobileDock / MobileNavDrawer). Reads
 * the persisted hub map once per navigation and returns a stable resolver.
 * `navTargets` is the full set of nav-item `to` values, used to tell single-
 * from multi-entry hubs (see pickHubTarget).
 */
export function useHubRouteMemory(navTargets: string[]): HubRouteMemory {
  const location = useLocation()
  const locationKey = `${location.pathname}${location.search}`
  const targetsKey = navTargets.join('|')

  // Re-read the persisted map whenever the path changes (a nav just landed),
  // so the next click resolves against fresh memory without subscribing to
  // storage events. `locationKey` is the intentional re-read trigger even
  // though `readHubMap` doesn't reference it.
  const map = useMemo(
    () => readHubMap(currentScope()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationKey],
  )
  // Stabilise the nav-target array by its joined key so `resolveTarget` keeps
  // a stable identity across renders that pass a fresh array literal.
  const targets = useMemo(
    () => navTargets,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetsKey],
  )
  const resolveTarget = useCallback(
    (to: string) => resolveHubTarget(to, map, targets),
    [map, targets],
  )
  return { resolveTarget }
}

export default useHubRouteMemory
